import { supabase } from './supabase';
import type { TransactionHead } from './supabase';

const mockTransactionHeads: TransactionHead[] = [
  {
    id: '1',
    name: 'Consultation Fee',
    type: 'income',
    note: 'Doctor consultation charges',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: '2',
    name: 'Lab Test Fee',
    type: 'income',
    note: 'Laboratory test charges',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: '3',
    name: 'Medicine Sales',
    type: 'income',
    note: 'Pharmacy medicine sales',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: '4',
    name: 'Utility Bills',
    type: 'expense',
    note: 'Electricity, water, internet bills',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: '5',
    name: 'Staff Salary',
    type: 'expense',
    note: 'Monthly staff salaries',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

const isSupabaseConfigured = () => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  return url && url !== 'https://placeholder.supabase.co';
};

export const transactionHeadsService = {
  async getAll(): Promise<TransactionHead[]> {
    if (!isSupabaseConfigured()) {
      return mockTransactionHeads;
    }

    try {
      const { data, error } = await supabase
        .from('transaction_heads')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching transaction heads:', error);
      return mockTransactionHeads;
    }
  },

  async getByType(type: 'income' | 'expense'): Promise<TransactionHead[]> {
    if (!isSupabaseConfigured()) {
      return mockTransactionHeads.filter(t => t.type === type);
    }

    try {
      const { data, error } = await supabase
        .from('transaction_heads')
        .select('*')
        .eq('type', type)
        .order('name', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching transaction heads by type:', error);
      return mockTransactionHeads.filter(t => t.type === type);
    }
  },

  async getById(id: string): Promise<TransactionHead | null> {
    if (!isSupabaseConfigured()) {
      return mockTransactionHeads.find(t => t.id === id) || null;
    }

    try {
      const { data, error } = await supabase
        .from('transaction_heads')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching transaction head:', error);
      return mockTransactionHeads.find(t => t.id === id) || null;
    }
  },

  async create(head: Omit<TransactionHead, 'id' | 'created_at' | 'updated_at'>): Promise<TransactionHead> {
    if (!isSupabaseConfigured()) {
      const newHead: TransactionHead = {
        ...head,
        id: Math.random().toString(36).substr(2, 9),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      mockTransactionHeads.unshift(newHead);
      return newHead;
    }

    try {
      const { data, error } = await supabase
        .from('transaction_heads')
        .insert([head])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating transaction head:', error);
      throw error;
    }
  },

  async update(id: string, updates: Partial<TransactionHead>): Promise<TransactionHead> {
    if (!isSupabaseConfigured()) {
      const index = mockTransactionHeads.findIndex(t => t.id === id);
      if (index !== -1) {
        mockTransactionHeads[index] = {
          ...mockTransactionHeads[index],
          ...updates,
          updated_at: new Date().toISOString(),
        };
        return mockTransactionHeads[index];
      }
      throw new Error('Transaction head not found');
    }

    try {
      const { data, error } = await supabase
        .from('transaction_heads')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating transaction head:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      const index = mockTransactionHeads.findIndex(t => t.id === id);
      if (index !== -1) {
        mockTransactionHeads.splice(index, 1);
      }
      return;
    }

    try {
      const { error } = await supabase
        .from('transaction_heads')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting transaction head:', error);
      throw error;
    }
  },
};
